import { Button, Card, contexts, FeedbackIcon, fromTheme, Label, Modal, styled, Text } from 'lib_ui-primitives';
import { constants, offlineResilientCommunication, socket } from 'lib_ui-services';
import lodash from 'lodash';
import { Fragment, memo, createElement as rc, useCallback, useContext, useEffect, useState } from 'react';
import useEventSink from '../../hooks/useEventSink';
import useNetwork from '../../hooks/useNetwork';
const { isEqual } = lodash;

import { Body, ButtonBar, CardHeader, ConfirmationCard, DialogButton, HeaderText } from './styles';

const SyncCard = styled(ConfirmationCard)`
    max-width: 500px;
`;

const SubText = styled(Text)`
    ${fromTheme('card', 'body', 'font')};
`;

const FeedbackLabel = styled(Label)`
    width: 100%;
    gap: 8px;
    justify-content: space-between;
    ${fromTheme('card', 'body', 'font')};
`;

const _p = {
    offlineResilientCommunication
};
export const _private = _p;
function SyncDialog({ id } = {}) {
    const {
        busy,
        online,
        pendingCount = 0,
        retryCount = 0,
        retryScheduled
    } = _p.offlineResilientCommunication.getStatus();
    const [syncing, setSyncing] = useState({ httpQueue: busy, httpSync: false });
    const [socketOnline, changeSocketState] = useState(false);
    const networkStatus = useNetwork();

    const session = useContext(contexts.SessionContext);
    const { allFeatureFlags = [] } = session;
    const [subscribe, publish] = useEventSink();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const unsubscribe = subscribe({ verb: 'update', namespace: 'application', relation: 'socket' }, payload => {
            changeSocketState(payload.isOnline);
        });
        changeSocketState(socket.getStatus());
        return () => unsubscribe();
    }, [subscribe]);

    const notificationContext = useContext(contexts.UiNotificationContext);
    useEffect(() => {
        const removeHandler = notificationContext.addTypeHandler(constants.notificationTypes.SYNC_BUSY, payload => {
            const { busy, source } = payload;
            setSyncing(prev => {
                const newValue = { ...prev, [source]: busy };
                if (!isEqual(prev, newValue)) {
                    return newValue;
                }
                return prev;
            });
        });
        return removeHandler;
    }, [notificationContext]);

    const WaitingForConnection = !online || !socketOnline || !networkStatus.isConnected;

    const showForceSync = online && allFeatureFlags.includes('canForceSync');
    const iconColor = WaitingForConnection ? 'error' : syncing.httpQueue || syncing.httpSync ? 'warning' : undefined;

    let title = 'No pending requests';
    let iconName = 'check-circle';
    if (syncing.httpQueue || syncing.httpSync) {
        title = 'Sync In Progress';
        iconName = 'refresh-cw';
    } else if (WaitingForConnection) {
        title = 'Waiting for Connection';
        iconName = 'cloud-off';
    }

    const onOk = () => setOpen(false);
    const onClick = useCallback(() => setOpen(true), [setOpen]);
    const forceSync = () => {
        if (!session.profile.userId && !session.profile._id)
            throw new Error('Unable to find the currently logged in user.');
        setOpen(false);
        publish(
            { userId: session.profile.userId ?? session.profile._id },
            { verb: 'persist', namespace: 'inventory', relation: 'inventory', type: 'forceInventorySync' }
        );
    };

    // prettier-ignore
    return rc(Fragment, {},
        rc(Button, { 
            id, 
            title, 
            clocking: syncing.httpQueue || syncing.httpSync, 
            name: 'notifications-button', 
            icon: iconName, 
            variant: 'secondary', 
            size: 'lg', 
            onClick ,
            badge: iconColor
        }),
        rc(Modal, { visible: open, id: 'syncDialogModal' },
            rc(SyncCard, null,
                rc(CardHeader, null, 
                    rc(FeedbackIcon,
                        {
                            color: iconColor,
                            variant: 'primary',
                            size: 'lg',
                            name: iconName,
                            key: '0'
                        }
                    ),
                    rc(HeaderText, null, 'Sync Status')),
                rc(Body, null,
                    rc(FeedbackLabel, null,
                        'Client to server communication: ',
                        rc(SubText, { title: 'Client to server communication' }, online ? 'Connected' : 'Disconnected')
                    ), rc(FeedbackLabel, null,
                        'Server to client communication: ',
                        rc(SubText, { title: 'Server to client communication' }, socketOnline ? 'Connected' : 'Disconnected')
                    ),
                    rc(FeedbackLabel, null,
                        'Pending requests: ',
                        rc(SubText, { title: 'Pending requests' }, pendingCount.toString())
                    ),
                    rc(FeedbackLabel, null,
                        'Syncing client to server: ',
                        rc(SubText, { title: 'Transmit active' }, busy ? 'Yes' : 'No')
                    ),
                    rc(FeedbackLabel, null,
                        'Syncing server to client: ',
                        rc(SubText, { title: 'Receive active' }, syncing.httpSync ? 'Yes' : 'No')
                    ),
                    retryCount > 0 && rc(FeedbackLabel, null,
                        'Retries: ',
                        rc(SubText, { title: 'Retry count' }, String(retryCount))
                    ),
                    (retryCount > 0 || retryScheduled) && rc(FeedbackLabel, null,
                        'Retry Scheduled: ',
                        rc(SubText, { title: 'Is retry scheduled' }, retryScheduled ? 'Yes' : 'No')
                    )
                ),
                rc(Card.Footer, null,
                    rc(ButtonBar, null,
                        showForceSync && rc(DialogButton, { value: 'Force Sync', onClick: forceSync, variant: 'secondary', size:'lg' }),
                        rc(DialogButton, { value: 'OK', onClick: onOk,  variant: 'primary' , size:'lg'})
                    )
                )
            )
        )
    );
}

export default memo(SyncDialog);
